import { formatInTimeZone } from 'date-fns-tz';

export type AssetClass = 'STK' | 'FUT' | 'CASH' | 'CRYPTO';

const NY_TIMEZONE = 'America/New_York';

type NyParts = {
  day: number;
  hour: number;
  minute: number;
  totalMinutes: number;
  nyIso: string;
};

function getNyParts(timestampMs: number): NyParts | null {
  if (!Number.isFinite(timestampMs)) return null;

  const date = new Date(timestampMs);
  if (Number.isNaN(date.getTime())) return null;

  const day = Number(formatInTimeZone(date, NY_TIMEZONE, 'i')) % 7;
  const hour = Number(formatInTimeZone(date, NY_TIMEZONE, 'H'));
  const minute = Number(formatInTimeZone(date, NY_TIMEZONE, 'm'));

  if (!Number.isFinite(day) || !Number.isFinite(hour) || !Number.isFinite(minute)) return null;

  return {
    day,
    hour,
    minute,
    totalMinutes: hour * 60 + minute,
    nyIso: formatInTimeZone(date, NY_TIMEZONE, "yyyy-MM-dd'T'HH:mm:ssXXX"),
  };
}

function validateStk(day: number, totalMinutes: number): { valid: boolean; reason?: string } {
  if (day === 0 || day === 6) return { valid: false, reason: 'STK weekend (Mon-Fri only)' };
  if (totalMinutes < 4 * 60) return { valid: false, reason: 'STK before 04:00 NY' };
  if (totalMinutes > 20 * 60) return { valid: false, reason: 'STK after 20:00 NY' };
  return { valid: true };
}

function validateFut(day: number, totalMinutes: number): { valid: boolean; reason?: string } {
  if (day === 6) return { valid: false, reason: 'FUT closed on Saturday' };
  if (day === 5 && totalMinutes >= 17 * 60) return { valid: false, reason: 'FUT closed after Friday 17:00 NY' };
  if (day === 0 && totalMinutes < 18 * 60) return { valid: false, reason: 'FUT opens Sunday 18:00 NY' };
  if (totalMinutes >= 17 * 60 && totalMinutes < 18 * 60) return { valid: false, reason: 'FUT daily maintenance break 17:00-18:00 NY' };
  return { valid: true };
}

function validateCash(day: number, totalMinutes: number): { valid: boolean; reason?: string } {
  if (day === 6) return { valid: false, reason: 'CASH closed on Saturday' };
  if (day === 5 && totalMinutes >= 17 * 60) return { valid: false, reason: 'CASH closed after Friday 17:00 NY' };
  if (day === 0 && totalMinutes < 17 * 60) return { valid: false, reason: 'CASH opens Sunday 17:00 NY' };
  return { valid: true };
}

export function isValidSession(assetClass: AssetClass, timestampMs: number): boolean {
  return getSessionInfo(assetClass, timestampMs).valid;
}

export function getSessionInfo(
  assetClass: AssetClass,
  timestampMs: number,
): {
  valid: boolean;
  reason?: string;
  nyIso: string;
} {
  const nyParts = getNyParts(timestampMs);
  if (!nyParts) {
    return {
      valid: false,
      reason: 'Invalid timestamp',
      nyIso: 'invalid',
    };
  }

  const { day, totalMinutes, nyIso } = nyParts;

  if (assetClass === 'CRYPTO') {
    return { valid: true, nyIso };
  }

  if (assetClass === 'STK') {
    const outcome = validateStk(day, totalMinutes);
    return { ...outcome, nyIso };
  }

  if (assetClass === 'FUT') {
    const outcome = validateFut(day, totalMinutes);
    return { ...outcome, nyIso };
  }

  const outcome = validateCash(day, totalMinutes);
  return { ...outcome, nyIso };
}
